import React, { useState } from 'react';

const ReportPage: React.FC = () => {
  const [formData, setFormData] = useState({
    reportType: '',
    itemId: '',
    description: '',
    email: '',
    name: '',
    evidence: null as File | null,
    attachScreenshot: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData({
        ...formData,
        evidence: e.target.files[0]
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.reportType) {
      setError('Please select a report type');
      return;
    }

    if (!formData.description) {
      setError('Please provide a description');
      return;
    }

    if (!formData.email) {
      setError('Please provide your email');
      return;
    }

    setError('');
    setSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);

      // Clear form
      setFormData({
        reportType: '',
        itemId: '',
        description: '',
        email: '',
        name: '',
        evidence: null,
        attachScreenshot: false
      });
    }, 1500);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Report an Issue</h1>

        <div className="bg-[#1F1A27] rounded-lg p-6 mb-6">
          <h2 className="text-xl font-medium mb-4">Reporting Guidelines</h2>
          <p className="text-[var(--color-muted)] mb-4">
            Use this form to report violations of our terms, inappropriate content, intellectual property infringement, or technical issues. Please provide as much detail as possible to help us investigate and resolve the issue quickly.
          </p>
          <div className="flex items-start gap-3 p-3 bg-[#7F56D9]/10 rounded-md border border-[#7F56D9]/20">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#7F56D9] flex-shrink-0 mt-0.5">
              <path d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
            </svg>
            <p className="text-sm">
              For urgent security vulnerabilities, please email
              <a href="mailto:security@developermarketplace.com" className="text-[#7F56D9] hover:underline ml-1">
                security@developermarketplace.com
              </a>
              {' '}directly instead of using this form.
            </p>
          </div>
        </div>

        {submitted ? (
          <div className="bg-[#1F1A27] rounded-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-green-500">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="text-2xl font-medium mb-3">Report Submitted</h2>
            <p className="text-[var(--color-muted)] mb-6">
              Thank you for your report. We take all reports seriously and will investigate the issue. You'll receive a confirmation email shortly, and we'll follow up with you if we need additional information.
            </p>
            <button
              className="btn-primary py-3 px-6"
              onClick={() => setSubmitted(false)}
            >
              Submit Another Report
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#1F1A27] rounded-lg p-6">
            {error && (
              <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="reportType" className="block mb-2 font-medium">
                Report Type <span className="text-red-400">*</span>
              </label>
              <select
                id="reportType"
                name="reportType"
                value={formData.reportType}
                onChange={handleInputChange}
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                required
              >
                <option value="">Select Report Type</option>
                <option value="content">Inappropriate Content</option>
                <option value="copyright">Copyright Infringement</option>
                <option value="terms">Terms of Service Violation</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Problem</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="itemId" className="block mb-2 font-medium">
                Item ID or URL <span className="text-[var(--color-muted)] text-sm">(if applicable)</span>
              </label>
              <input
                type="text"
                id="itemId"
                name="itemId"
                value={formData.itemId}
                onChange={handleInputChange}
                placeholder="e.g., Product ID or URL of the page with the issue"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="description" className="block mb-2 font-medium">
                Description <span className="text-red-400">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={5}
                placeholder="Please provide a detailed description of the issue"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                required
              ></textarea>
            </div>

            <div className="mb-6">
              <label htmlFor="evidence" className="block mb-2 font-medium">
                Evidence or Documentation
              </label>
              <input
                type="file"
                id="evidence"
                name="evidence"
                onChange={handleFileChange}
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                accept=".jpg,.jpeg,.png,.pdf,.zip"
              />
              <p className="text-xs text-[var(--color-muted)] mt-1">
                Accepted formats: .jpg, .png, .pdf, .zip (max 10MB)
              </p>
            </div>

            <div className="mb-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="attachScreenshot"
                  name="attachScreenshot"
                  checked={formData.attachScreenshot}
                  onChange={handleCheckboxChange}
                  className="mr-2"
                />
                <label htmlFor="attachScreenshot" className="text-sm">
                  Automatically attach browser information and screenshot of current page
                </label>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="email" className="block mb-2 font-medium">
                Your Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@example.com"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
                required
              />
            </div>

            <div className="mb-6">
              <label htmlFor="name" className="block mb-2 font-medium">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="John Doe"
                className="w-full bg-[#2A2633] border border-white/10 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
              />
            </div>

            <div className="text-right">
              <button
                type="submit"
                className="btn-primary py-3 px-6 flex items-center justify-center"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Report'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportPage;
